import { useState } from 'react'
import { useCards, useArchiveCard, type Card } from '../lib/cards'
import EmptyState from '../components/EmptyState'
import CardForm from '../components/CardForm'

export default function CardsPage() {
  const [editingCard, setEditingCard] = useState<Card | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const { data: listResponse, isLoading, error } = useCards()
  const archiveMutation = useArchiveCard()

  const list = listResponse?.cards ?? []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="inline-flex items-center gap-2 text-[var(--color-danger)]">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span>Erro ao carregar cartões</span>
        </div>
      </div>
    )
  }

  const handleEdit = (card: Card) => {
    setEditingCard(card)
    setIsFormOpen(true)
  }

  const handleDelete = (cardId: string) => {
    archiveMutation.mutate(cardId)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingCard(null)
  }

  const handleNewCard = () => {
    setEditingCard(null)
    setIsFormOpen(true)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Cartões</h1>
        <button
          type="button"
          onClick={handleNewCard}
          className="rounded-lg bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-brand-600)] transition-colors"
        >
          + Novo cartão
        </button>
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25a.75.75 0 01.75-.75h13.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75h-13.5a.75.75 0 01-.75-.75v-13.5z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8h18M3 12h18M3 16h18" />
            </svg>
          }
          title="Nenhum cartão cadastrado"
          description="Adicione cartões para rastrear suas compras e gastos."
          action={{
            label: 'Adicionar cartão',
            onClick: handleNewCard,
          }}
        />
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((card) => (
            <CardCard
              key={card.id}
              card={card}
              onEdit={() => handleEdit(card)}
              onDelete={() => handleDelete(card.id)}
              isDeleting={archiveMutation.isPending}
            />
          ))}
        </div>
      )}

      <CardForm
        open={isFormOpen}
        onClose={handleCloseForm}
        card={editingCard}
      />
    </div>
  )
}

interface CardCardProps {
  card: Card
  onEdit: () => void
  onDelete: () => void
  isDeleting: boolean
}

function CardCard({ card, onEdit, onDelete, isDeleting }: CardCardProps) {
  const typeLabel = (card_type: Card['card_type']) => {
    return card_type === 'credit' ? 'Crédito' : card_type === 'debit' ? 'Débito' : 'Pré-pago'
  }

  const typeStyle = (card_type: Card['card_type']) => {
    return card_type === 'credit'
      ? 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)]'
      : 'bg-[var(--color-surface-subtle)] text-[var(--color-text-muted)]'
  }

  const getBrandIcon = (card_type: Card['card_type']) => {
    switch (card_type) {
      case 'credit':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L1 7v10l11 5 11-5V7L12 2z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )
      case 'debit':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <rect x="2" y="2" width="20" height="20" rx="2" ry="2" />
            <line x1="12" y1="6" x2="12" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <line x1="6" y1="12" x2="18" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12l3 3 5-5" />
          </svg>
        )
    }
  }

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {getBrandIcon(card.card_type)}
          <div>
            <p className="text-sm font-medium text-[var(--color-text)]">{card.name}</p>
            <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${typeStyle(card.card_type)}`}>
              {typeLabel(card.card_type)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="rounded-full p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)] transition-colors"
            title="Editar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.25 3.25l6.5 6.5H7.75L11.25 3.25z" />
              <path fillRule="evenodd" d="M11.25 3.25v9.5M17.75 9.75H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting}
            className="rounded-full p-1 text-[var(--color-text-muted)] hover:text-[var(--color-danger)] hover:bg-[var(--color-danger-light)] transition-colors disabled:opacity-50"
            title="Excluir"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M16.5 4.5V18M7.5 4.5v13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M4.5 7.5h15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span className={`h-2 w-2 rounded-full ${card.is_active ? 'bg-[var(--color-success)]' : 'bg-[var(--color-text-muted)]'}`} />
        <span className="text-xs text-[var(--color-text-muted)]">
          {card.is_active ? 'Ativo' : 'Inativo'}
        </span>
        {card.color && (
          <>
            <span className="text-xs text-[var(--color-text-muted)]">|</span>
            <span className="text-xs text-[var(--color-text-muted)]">Cor: {card.color}</span>
          </>
        )}
      </div>
    </div>
  )
}
